import Header from "components/Header";
import NavigationTabs, { NavigationTab } from "components/NavigationTabs";
import { BOOKMARK_LISTNAME_FAVORITE } from "constants/config";
import path from "constants/path";
import { useBookmarks } from "features/Bookmarks";
import {
  getBookmarksByType,
  IBookmarkItem,
  IBookmarkListItem,
} from "helpers/bookmark";
import useLabels from "hooks/useLabels";
import { useRouter } from "next/router";
import { useState } from "react";
import { BookmarkItemsType } from "store/slices/bookmark";
import BookmarkListItemActionButton from "./BookmarkListItemActionButton";
import ConfirmDeleteListModal from "./ConfirmDeleteListModal";
import EditListModal from "./EditListModal";
import PaperBookmarkList from "./PaperBookmarkList";
import ThreadBookmarkList from "./ThreadBookmarkList";

type BookmarkListProps = {
  bookmarkList: IBookmarkListItem;
  bookmarkItems: BookmarkItemsType;
};

enum BookmarkTypeTab {
  PaperTab = "paper tab",
  SearchTab = "search tab",
  ThreadTab = "thread tab",
}

/**
 * @component BookmarkList
 * @description The component that shows the contents of a bookmark list. consists of a main nav and a tabbed menu to view bookmarks of various types.
 */
function BookmarkList({ bookmarkList, bookmarkItems }: BookmarkListProps) {
  const router = useRouter();

  const isFavorite = bookmarkList.text_label === BOOKMARK_LISTNAME_FAVORITE;
  const [selectedTab, setSelectedTab] = useState<BookmarkTypeTab>(
    BookmarkTypeTab.PaperTab
  );

  const [labels] = useLabels("features.bookmark-lists.bookmark-list-detail");

  const [isEditListModalOpen, setIsEditListModalOpen] = useState(false);
  const [isConfirmDeleteListModalOpen, setIsConfirmDeleteListModalOpen] =
    useState(false);

  const handleTabClick = (tabId: string) => {
    setSelectedTab(tabId as BookmarkTypeTab);
  };

  const { deleteBookmarkItem, deleteBookmarkList } = useBookmarks();

  const handleConfirmDeleteList = async () => {
    setIsConfirmDeleteListModalOpen(false);
    await deleteBookmarkList(bookmarkList.id);
    router.replace(path.RESEARCH_HUB_LISTS);
  };

  const { paperBookmarks, searchBookmarks, threadBookmarks } =
    getBookmarksByType(bookmarkList.id, bookmarkItems);

  const handleClickRemoveBookmark = async (bookmarkItem: IBookmarkItem) => {
    await deleteBookmarkItem(bookmarkItem);
  };

  const tabs: NavigationTab[] = [
    {
      id: BookmarkTypeTab.PaperTab,
      label: labels["papers"],
    },
    {
      id: BookmarkTypeTab.ThreadTab,
      label: labels["threads"],
    },
  ];

  return (
    <>
      <div data-testid="bookmark-list-detail">
        <Header
          showBackButton
          title={bookmarkList?.text_label}
          actions={
            !isFavorite && (
              <BookmarkListItemActionButton
                onClickEditList={() => setIsEditListModalOpen(true)}
                onClickDeleteList={() => setIsConfirmDeleteListModalOpen(true)}
              />
            )
          }
          className="border-none"
        />
        <NavigationTabs
          tabs={tabs}
          selectedTab={selectedTab}
          onTabClick={handleTabClick}
          sticky={true}
        />
        <div className="flex justify-center px-4 md:px-6">
          <div className="w-full max-w-page">
            {selectedTab === BookmarkTypeTab.PaperTab ? (
              <PaperBookmarkList
                bookmarkListTitle={bookmarkList?.text_label ?? ""}
                bookmarks={paperBookmarks}
                handleClickRemoveBookmark={handleClickRemoveBookmark}
              />
            ) : (
              <ThreadBookmarkList
                bookmarks={[...threadBookmarks, ...searchBookmarks]}
              />
            )}
          </div>
        </div>
      </div>
      <EditListModal
        open={isEditListModalOpen}
        onClose={() => setIsEditListModalOpen(false)}
        bookmarkList={bookmarkList}
      />
      <ConfirmDeleteListModal
        open={isConfirmDeleteListModalOpen}
        onConfirm={handleConfirmDeleteList}
        onClose={() => setIsConfirmDeleteListModalOpen(false)}
      />
    </>
  );
}

export default BookmarkList;
